import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getRecentActivityList } from "../api/recentActivity.api";
import type { RecentActivityItem } from "../types/recentActivity.types";

const CACHE_KEY = "dashboard_recent_activity";
const CACHE_DURATION_MS = 60_000;
const FEED_SIZE = 10;

interface CacheEntry {
  data: RecentActivityItem[];
  timestamp: number;
}

export interface UseRecentActivityFeedReturn {
  data: RecentActivityItem[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const getCachedData = (): RecentActivityItem[] | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.timestamp < CACHE_DURATION_MS && Array.isArray(entry.data)) {
      return entry.data;
    }
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    sessionStorage.removeItem(CACHE_KEY);
  }
  return null;
};

const cacheData = (data: RecentActivityItem[]): void => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() } satisfies CacheEntry));
  } catch {
    return;
  }
};

export const useRecentActivityFeed = (): UseRecentActivityFeedReturn => {
  const [data, setData] = useState<RecentActivityItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const { run } = useLatestRequest();

  const fetchFeed = useCallback(async (forceRefresh = false) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!forceRefresh) {
      const cached = getCachedData();
      if (cached) {
        setData(cached);
        setError(null);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await run((signal) =>
        getRecentActivityList(0, FEED_SIZE, { signal }),
      );
      if (!response) return;
      if (requestId !== requestIdRef.current) return;
      cacheData(response.list);
      setData(response.list);
    } catch (requestError: unknown) {
      if (requestId !== requestIdRef.current) return;
      setError(getApiErrorMessage(requestError, "Failed to fetch recent activity"));
      setData(null);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [run]);

  const refetch = useCallback(async () => {
    await fetchFeed(true);
  }, [fetchFeed]);

  useEffect(() => {
    void fetchFeed();
    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchFeed]);

  return { data, isLoading, error, refetch };
};
